import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL 
  ? process.env.REACT_APP_API_BASE_URL 
  : (process.env.NODE_ENV === 'production'
      ? 'https://card-profit-optimizer-0dde4aef6f85.herokuapp.com'
      : 'http://localhost:5000'
    );

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  const checkAuthStatus = async () => {
    try {
      const response = await fetch('/api/auth/check', {
        credentials: 'include'
      });
      const data = await response.json();
      setIsAuthenticated(data.authenticated);
      if (data.authenticated) {
        setUser(data.user);
      }
    } catch (error) {
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const login = async (email, password) => {
    try {
      const normalizedEmail = email.trim().toLowerCase();
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: normalizedEmail, password }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setIsAuthenticated(true);
        setUser(data.user);
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  const logout = async () => {
    try {
      await fetch('/api/auth/logout', {
        credentials: 'include'
      });
    } finally {
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  // Free-tier signup: no Stripe, no email verification. The API signs the user
  // in on success, so we adopt the returned user directly.
  const registerFree = async (email, password, emailOptIn) => {
    const normalizedEmail = email.trim().toLowerCase();
    const response = await fetch('/api/auth/register-free', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: normalizedEmail, password, email_opt_in: emailOptIn }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Could not create your account.');
    setIsAuthenticated(true);
    setUser(data.user);
    return data.user;
  };

  const register = async (email, password) => {
    try {
      const normalizedEmail = email.trim().toLowerCase();
      console.log('Attempting registration...');
      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email: normalizedEmail, password }),
      });

      console.log('Registration response status:', response.status);
      const data = await response.json();
      console.log('Registration response data:', data);

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      console.log('Registration successful:', data);
      return true;
    } catch (error) {
      console.error('Registration error details:', error);
      return false;
    }
  };

  // Add this check before any redirects
  const shouldRedirectToLogin = (pathname) => {
    // Don't redirect if we're on the home page or public routes
    const publicRoutes = ['/', '/home', '/terms', '/privacy', '/reset-password'];
    return !publicRoutes.includes(pathname);
  };

  if (loading) {
    return <div>Loading...</div>; // Or your loading component
  }

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      loading,
      user,
      login,
      logout,
      register,
      registerFree,
      checkAuthStatus
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext); 

